import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError, staleCopy } from "../api";
import type { MemoryEntry, MemoryIndexEntry, MemoryProposal, MemoryTree } from "../types";
import { ChevronLeftIcon, MenuIcon, PlusIcon } from "../icons";
import { Avatar } from "../avatar";
import { useIsNarrow } from "../viewport";
import { usePhoneDetail } from "./phonePage";
import { StaleNotice } from "../components/StaleNotice";

/** Which namespace a selection lives in: the person's global view, or one profile's private one. */
interface Selection {
	profile?: string;
	path: string;
}

const LEAVE = "有未保存的修改，确定离开？";
/** What a topic may hold, in bytes. */
const LIMIT = 8192;

/** `2026-09-18` as `9/18`; anything else as it is. */
function shortDate(date: string | undefined): string {
	const m = date?.match(/^\d{4}-(\d{2})-(\d{2})/);
	return m ? `${Number(m[1])}/${Number(m[2])}` : (date ?? "");
}

/** A topic being created: nothing on disk yet. */
function blank(selected: Selection): MemoryEntry {
	const namespace = selected.profile ? "private" : selected.path.startsWith("me/") ? "me" : "shared";
	return { path: selected.path, namespace, title: selected.path, body: "", bytes: 0, version: "" };
}

export function Memory({ onMenu }: { onMenu: () => void }) {
	const [tree, setTree] = useState<MemoryTree | null>(null);
	const [proposals, setProposals] = useState<MemoryProposal[]>([]);
	const [selected, setSelected] = useState<Selection | null>(null);
	/* The one page whose whole purpose is hand-editing, so picking another topic, or leaving the
	   editor on a phone, asks before it drops the edit. */
	const dirtyNow = useRef(false);
	const phone = usePhoneDetail(useIsNarrow(), "memoryPage", () => {
		if (dirtyNow.current && !confirm(LEAVE)) {
			history.pushState({ memoryPage: 1 }, "");
			return;
		}
		setSelected(null);
	});
	const pickTopic = (next: Selection) => {
		if (dirty && !confirm(LEAVE)) return;
		setSelected(next);
		phone.opened();
	};
	const [entry, setEntry] = useState<MemoryEntry | null>(null);
	const [body, setBody] = useState("");
	const [description, setDescription] = useState("");
	const [dirty, setDirty] = useState(false);
	dirtyNow.current = dirty;
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	/** A save refused because the page changed after it was loaded, and what it holds now. */
	const [stale, setStale] = useState<{ current: MemoryEntry | null } | null>(null);

	const show = useCallback((next: MemoryEntry) => {
		setEntry(next);
		setBody(next.body);
		setDescription(next.description ?? "");
		setDirty(false);
		setStale(null);
	}, []);

	const refresh = useCallback(async () => {
		try {
			const [next, waiting] = await Promise.all([api.memoryTree(), api.memoryProposals()]);
			setTree(next);
			setProposals(waiting.proposals);
		} catch (err) {
			setError(err instanceof ApiError ? err.message : "加载记忆失败");
		}
	}, []);

	useEffect(() => {
		void refresh();
	}, [refresh]);

	useEffect(() => {
		if (!selected) return;
		setEntry(null);
		setDirty(false);
		setStale(null);
		void api
			.memoryFile(selected.path, selected.profile)
			.then(({ entry }) => show(entry))
			.catch((err) => {
				if (err instanceof ApiError && err.status === 404) show(blank(selected));
				else setError(err instanceof ApiError ? err.message : "读取失败");
			});
	}, [selected, show]);

	/** Save over the version the editor holds, or over `ifVersion` once the person chose to overwrite. */
	async function save(ifVersion = entry?.version) {
		if (!selected) return;
		setBusy(true);
		setError(null);
		setStale(null);
		try {
			const { entry } = await api.memoryWrite({ path: selected.path, body, description: description || undefined, ifVersion }, selected.profile);
			show(entry);
			await refresh();
		} catch (err) {
			const copy = staleCopy<MemoryEntry>(err);
			if (copy) setStale(copy);
			else setError(err instanceof ApiError ? err.message : "保存失败");
		} finally {
			setBusy(false);
		}
	}

	async function remove() {
		if (!selected || !confirm(`删除 ${selected.path}？`)) return;
		try {
			await api.memoryDelete(selected.path, selected.profile);
			dirtyNow.current = false;
			phone.back();
			setEntry(null);
			await refresh();
		} catch (err) {
			setError(err instanceof ApiError ? err.message : "删除失败");
		}
	}

	function create(namespace: "me" | "shared" | "private", profile?: string) {
		const name = prompt("主题文件名（小写字母、数字、连字符）", "")?.trim();
		if (!name) return;
		if (!/^[a-z0-9][a-z0-9-]*$/.test(name)) return setError("文件名只能用小写字母、数字和连字符");
		pickTopic({ profile, path: `${namespace}/${name}.md` });
	}

	async function answer(p: MemoryProposal, confirmed: boolean, text?: string) {
		setError(null);
		try {
			if (confirmed) await api.memoryConfirm(p.id, text === p.text ? undefined : text);
			else await api.memoryReject(p.id);
			await refresh();
		} catch (err) {
			setError(err instanceof ApiError ? err.message : "操作失败");
		}
	}

	const isSel = (s: Selection) => selected?.path === s.path && selected?.profile === s.profile;

	return (
		<main className={`chat memory split-page${selected ? " detail-open" : ""}`}>
			{error && (
				<p className="banner error">
					{error}
					<button className="banner-close" title="关闭" onClick={() => setError(null)}>
						×
					</button>
				</p>
			)}
			<div className="split">
				<section className="split-list">
					<header className="chat-head page-head">
						<button className="ghost sidebar-toggle icon-btn" aria-label="菜单" onClick={onMenu}>
							<MenuIcon size={22} />
						</button>
						<div className="titles">
							<span className="title">记忆</span>
						</div>
					</header>
					<div className="split-scroll">
						{tree === null ? (
							<p className="hint list-hint">加载中…</p>
						) : (
							<>
								{proposals.length > 0 && <Proposals items={proposals} onAnswer={(p, ok, text) => void answer(p, ok, text)} />}
								<Group label="我的" entries={tree.me} isSel={isSel} onPick={(p) => pickTopic({ path: p })} onNew={() => create("me")} />
								<Group label="共享" entries={tree.shared} isSel={isSel} onPick={(p) => pickTopic({ path: p })} onNew={() => create("shared")} />
								{tree.profiles.map((p) => (
									<Group
										key={p.name}
										label={`${p.name} 私有`}
										profile={p.name}
										entries={p.entries}
										isSel={isSel}
										onPick={(path) => pickTopic({ profile: p.name, path })}
										onNew={() => create("private", p.name)}
									/>
								))}
							</>
						)}
					</div>
				</section>
				<section className="split-detail">
					{selected && (
						<>
							<header className="detail-head">
								<button className="ghost icon-btn detail-back" aria-label="返回" title="返回" onClick={phone.back}>
									<ChevronLeftIcon size={24} />
								</button>
								<span className="detail-titles">
									<span className="detail-title">{entry?.title ?? selected.path}</span>
									<span className="detail-sub mono">
										{selected.profile ? `${selected.profile} · ` : ""}
										{selected.path}
									</span>
								</span>
								{dirty && <span className="pill tone-warn">未保存</span>}
								{entry && entry.bytes > 0 && (
									<button className="head-btn danger" onClick={() => void remove()}>
										删除
									</button>
								)}
								<button className="head-btn primary" onClick={() => void save()} disabled={busy || !dirty || !body.trim()}>
									{busy ? "保存中…" : "保存"}
								</button>
							</header>
							<div className="detail-body">
								{entry === null ? (
									<p className="hint">加载中…</p>
								) : (
									<div className="memory-editor">
										<p className="memory-meta">
											<span>{entry.updated ? `更新于 ${entry.updated}` : "未保存"}</span>
											<span className="memory-bytes">
												{entry.bytes} / {LIMIT} 字节
											</span>
											<span className="meter" aria-hidden="true">
												<span style={{ width: `${Math.min(100, (entry.bytes / LIMIT) * 100)}%` }} />
											</span>
										</p>
										<label className="field-card">
											<span>简介</span>
											<input value={description} onChange={(e) => (setDescription(e.target.value), setDirty(true))} />
										</label>
										<textarea
											className="memory-body"
											aria-label="正文"
											value={body}
											rows={20}
											onChange={(e) => (setBody(e.target.value), setDirty(true))}
										/>
										{stale && (
											<StaleNotice
												onReload={() => (show(stale.current ?? blank(selected)), void refresh())}
												onOverwrite={() => void save(stale.current?.version ?? "")}
											/>
										)}
									</div>
								)}
							</div>
						</>
					)}
				</section>
			</div>
		</main>
	);
}

function Group({
	label,
	profile,
	entries,
	isSel,
	onPick,
	onNew,
}: {
	label: string;
	profile?: string;
	entries: MemoryIndexEntry[];
	isSel: (s: Selection) => boolean;
	onPick: (path: string) => void;
	onNew: () => void;
}) {
	return (
		<>
			<div className="list-label with-action">
				{profile && <Avatar name={profile} size={20} />}
				<span>{label}</span>
				<button className="icon-btn small" aria-label={`在 ${label} 中新建主题`} title="新建主题" onClick={onNew}>
					<PlusIcon size={18} />
				</button>
			</div>
			<div className="row-group">
				{entries.length === 0 && <p className="hint list-hint">暂无</p>}
				{entries.map((e) => (
					<button key={e.path} className={`list-row${isSel({ profile, path: e.path }) ? " current" : ""}`} onClick={() => onPick(e.path)}>
						<span className="list-row-head">
							<span className="list-row-title">{e.title}</span>
							<span className="list-row-when">{shortDate(e.updated)}</span>
						</span>
						<span className="list-row-sub">{e.description ?? e.path}</span>
					</button>
				))}
			</div>
		</>
	);
}

/** Lines an agent learned that may belong on a wider page; nothing reaches it until confirmed here. */
function Proposals({ items, onAnswer }: { items: MemoryProposal[]; onAnswer: (p: MemoryProposal, confirmed: boolean, text?: string) => void }) {
	return (
		<>
			<h3 className="list-label tone-warn">待确认 · {items.length}</h3>
			<div className="proposals">
				{items.map((p) => (
					<ProposalCard key={p.id} proposal={p} onAnswer={onAnswer} />
				))}
			</div>
		</>
	);
}

function ProposalCard({ proposal: p, onAnswer }: { proposal: MemoryProposal; onAnswer: (p: MemoryProposal, confirmed: boolean, text?: string) => void }) {
	const [text, setText] = useState(p.text);
	return (
		<div className="proposal-card">
			<span className="proposal-target">
				{p.target === "me" ? "加入我的画像" : "加入共享"}
				{p.replacing ? `，替换「${p.replacing}」` : ""}
			</span>
			<input aria-label="内容" value={text} onChange={(e) => setText(e.target.value)} />
			<span className="proposal-from">来自 {p.from.map((f) => `${f.profile}${f.text ? `：${f.text}` : "（已不在）"}`).join("；")}</span>
			<div className="proposal-actions">
				<button className="primary" onClick={() => onAnswer(p, true, text.trim() || p.text)}>
					确认
				</button>
				<button onClick={() => onAnswer(p, false)}>拒绝</button>
			</div>
		</div>
	);
}
